import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Save, Copy, CheckCircle, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import QRCode from "qrcode";
import { API } from "@/lib/api";

const SERVICE_TYPES = [
  "Ironing",
  "Wash and Fold",
  "Wash and Iron",
  "Dry Cleaning",
  "Tailoring",
  "Other Services",
  "Commerce",
];

const initialLineItem = { service_type: "", custom_service: "", quantity: 1, cost_price: 0, selling_price: 0 };

const DEFAULT_PAYMENT_LINK = "https://razorpay.me/@pokus";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }).format(amount);

export const CreateVendorInvoice = () => {
  const navigate = useNavigate();
  const customerPdfRef = useRef(null);

  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdInvoice, setCreatedInvoice] = useState(null);
  const [copied, setCopied] = useState(false);

  const [formData, setFormData] = useState({
    vendor_id: "",
    invoice_date: new Date(),
    delivery_fee: 0,
    discount: 0,
    customer_name: "",
    customer_phone: "",
    contact_id: "",
    rm_name: "",
    payee_name: "",
    payment_link: "",
  });

  const [lineItems, setLineItems] = useState([{ ...initialLineItem }]);
  const [rmList, setRmList] = useState([]);
  const [payeeList, setPayeeList] = useState([]);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");

  const [autoGenerateLink, setAutoGenerateLink] = useState(true);
  const [razorpayConfigured, setRazorpayConfigured] = useState(false);
  const [generatedLink, setGeneratedLink] = useState("");

  const activePaymentLink = autoGenerateLink
    ? (generatedLink || DEFAULT_PAYMENT_LINK)
    : (formData.payment_link || "").trim() || DEFAULT_PAYMENT_LINK;

  useEffect(() => {
    QRCode.toDataURL(activePaymentLink, {
      width: 120, margin: 1, color: { dark: "#0047FF", light: "#ffffff" },
    }).then(setQrCodeDataUrl).catch(console.error);
  }, [activePaymentLink]);

  useEffect(() => {
    fetch(`${API}/razorpay/config`)
      .then((r) => (r.ok ? r.json() : { configured: false }))
      .then((d) => setRazorpayConfigured(Boolean(d?.configured)))
      .catch(() => setRazorpayConfigured(false));
  }, []);

  useEffect(() => {
    fetchVendors();
    fetchStaff();
    const handleMessage = (e) => {
      if (e.data?.customer) {
        setFormData((prev) => ({
          ...prev,
          customer_name: e.data.customer.name || prev.customer_name,
          customer_phone: e.data.customer.phone || prev.customer_phone,
          contact_id: e.data.customer.contact_id || prev.contact_id,
        }));
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await fetch(`${API}/vendors`);
      setVendors(await response.json());
    } catch {
      toast.error("Failed to load vendors");
    }
  };

  const fetchStaff = async () => {
    try {
      const [rmRes, payeeRes] = await Promise.all([
        fetch(`${API}/staff?team=Relations+Manager`),
        fetch(`${API}/staff?team=Payee`),
      ]);
      setRmList(await rmRes.json());
      setPayeeList(await payeeRes.json());
    } catch {
      console.error("Failed to load staff");
    }
  };

  const handleVendorChange = (vendorId) => {
    const vendor = vendors.find((v) => v.id === vendorId);
    setSelectedVendor(vendor);
    setFormData((prev) => ({ ...prev, vendor_id: vendorId }));
    if (vendor && lineItems[0].service_type === "") {
      setLineItems([{ ...initialLineItem, service_type: vendor.service_type }]);
    }
  };

  const calculations = useMemo(() => {
    const vendor_subtotal = lineItems.reduce((sum, item) => sum + item.quantity * item.cost_price, 0);
    const customer_subtotal = lineItems.reduce((sum, item) => sum + item.quantity * item.selling_price, 0);
    const pokus_fees = customer_subtotal - vendor_subtotal;
    const delivery_fee = parseFloat(formData.delivery_fee) || 0;
    const discount = parseFloat(formData.discount) || 0;
    const vendor_total = vendor_subtotal + delivery_fee;
    const customer_total = customer_subtotal + delivery_fee - discount;
    return { vendor_subtotal, customer_subtotal, pokus_fees, delivery_fee, discount, vendor_total, customer_total, subtotal: vendor_subtotal, total_payable: vendor_total };
  }, [lineItems, formData.delivery_fee, formData.discount]);

  const handleInputChange = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleLineItemChange = (index, field, value) => {
    setLineItems((prev) => {
      const updated = [...prev];
      if (field === "service_type") {
        updated[index] = { ...updated[index], service_type: value, custom_service: "" };
      } else if (field === "custom_service") {
        updated[index] = { ...updated[index], custom_service: value };
      } else if (field === "quantity") {
        updated[index] = { ...updated[index], quantity: parseFloat(value) || 0 };
      } else {
        updated[index] = { ...updated[index], [field]: parseFloat(value) || 0 };
      }
      return updated;
    });
  };

  const addLineItem = () => setLineItems((prev) => [...prev, { ...initialLineItem }]);
  const removeLineItem = (index) =>
    lineItems.length > 1 && setLineItems((prev) => prev.filter((_, i) => i !== index));

  const validateForm = () => {
    if (!formData.vendor_id) { toast.error("Please select a vendor"); return false; }
    if (!formData.customer_name.trim()) { toast.error("Customer name is required"); return false; }
    if (!formData.customer_phone.trim()) { toast.error("Customer phone is required"); return false; }
    const valid = lineItems.filter((i) => i.service_type && i.quantity > 0 && i.cost_price > 0 && i.selling_price > 0);
    if (valid.length === 0) { toast.error("At least one valid line item with CP and SP is required"); return false; }
    return true;
  };

  const attachRazorpayLink = async (invoice) => {
    try {
      const rawDigits = String(invoice.phone || "").replace(/\D/g, "");
      if (rawDigits.length < 10) {
        toast.warning("Auto-link skipped: customer phone must be at least 10 digits");
        return invoice;
      }
      const phone = rawDigits.length === 10 ? `+91${rawDigits}` : `+${rawDigits}`;

      const linkRes = await fetch(`${API}/razorpay/links`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: invoice.total_payable,
          currency: "INR",
          description: `Invoice ${invoice.invoice_number} — Pokus Technologies`,
          customer_name: invoice.customer_name,
          customer_phone: phone,
          reference_id: invoice.invoice_number,
          invoice_id: invoice.id,
        }),
      });
      if (!linkRes.ok) {
        const err = await linkRes.json().catch(() => ({}));
        throw new Error(err.detail || "Razorpay link creation failed");
      }
      const link = await linkRes.json();
      if (!link?.short_url) throw new Error("Razorpay returned no short URL");

      const patchRes = await fetch(`${API}/invoices/${invoice.id}/payment-link`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ payment_link: link.short_url }),
      });
      if (!patchRes.ok) throw new Error("Failed to attach Razorpay link to invoice");
      const updated = await patchRes.json();
      const finalLink = updated.payment_link || link.short_url;

      setGeneratedLink(finalLink);
      const freshQr = await QRCode.toDataURL(finalLink, {
        width: 120, margin: 1, color: { dark: "#0047FF", light: "#ffffff" },
      }).catch(() => null);
      if (freshQr) setQrCodeDataUrl(freshQr);
      // Let React commit the updated preview before html2canvas snapshot.
      await new Promise((r) => setTimeout(r, 150));

      toast.success("Razorpay link generated & attached");
      return updated;
    } catch (err) {
      console.error("Auto-generate Razorpay link failed:", err);
      toast.error(err.message ? `Razorpay: ${err.message}` : "Could not auto-generate Razorpay link");
      return invoice;
    }
  };

  const generateCustomerPDF = async (invoice) => {
    if (!customerPdfRef.current) return;
    try {
      await new Promise((r) => setTimeout(r, 500));
      const canvas = await html2canvas(customerPdfRef.current, {
        scale: 2, useCORS: true, logging: false, backgroundColor: "#ffffff",
      });
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const imgData = canvas.toDataURL("image/png");

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`customer_invoice_${invoice.invoice_number}.pdf`);
    } catch (err) {
      console.error("Customer PDF error:", err);
      toast.error("Failed to generate customer PDF");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const validLineItems = lineItems.filter((i) => i.service_type && i.quantity > 0 && i.cost_price > 0 && i.selling_price > 0);

      // Vendor invoice uses CP/SP, customer invoice uses SP as price_per_unit
      const [vendorRes, customerRes] = await Promise.all([
        fetch(`${API}/vendor-invoices`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            vendor_id: formData.vendor_id,
            invoice_date: format(formData.invoice_date, "yyyy-MM-dd"),
            line_items: validLineItems.map((i) => ({
              service_type: (i.service_type === "Other Services" || i.service_type === "Commerce")
                ? i.custom_service : i.service_type,
              quantity: i.quantity,
              cost_price: i.cost_price,
              selling_price: i.selling_price,
            })),
            delivery_fee: formData.delivery_fee,
            discount: formData.discount,
            customer_name: formData.customer_name,
            customer_phone: formData.customer_phone,
            contact_id: formData.contact_id,
            rm_name: formData.rm_name,
            payee_name: formData.payee_name,
          }),
        }),
        fetch(`${API}/invoices`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            customer_name: formData.customer_name,
            phone: formData.customer_phone,
            contact_id: formData.contact_id,
            invoice_date: format(formData.invoice_date, "yyyy-MM-dd"),
            line_items: validLineItems.map((i) => ({
              service_type: i.service_type,
              custom_service: i.custom_service || "",
              quantity: i.quantity,
              price_per_unit: i.selling_price,
            })),
            discount: formData.discount,
            delivery_fee: formData.delivery_fee,
            payment_link: activePaymentLink,
          }),
        }),
      ]);

      if (!vendorRes.ok) throw new Error("Failed to create vendor invoice");
      if (!customerRes.ok) throw new Error("Failed to create customer invoice");

      const vendorInvoice = await vendorRes.json();
      let customerInvoice = await customerRes.json();

      if (autoGenerateLink && razorpayConfigured) {
        customerInvoice = await attachRazorpayLink(customerInvoice);
      }

      setCreatedInvoice(vendorInvoice);
      await generateCustomerPDF(customerInvoice);
      setShowSuccess(true);
      toast.success("Invoice saved & customer PDF downloaded!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to create invoice");
    } finally {
      setLoading(false);
    }
  };

  const signingLink = createdInvoice
    ? `${window.location.origin}/sign/${createdInvoice.signing_token}`
    : "";

  const copyLink = () => {
    navigator.clipboard.writeText(signingLink);
    setCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setCopied(false), 2500);
  };

  const resetForm = () => {
    setShowSuccess(false);
    setCreatedInvoice(null);
    setCopied(false);
    setSelectedVendor(null);
    setFormData({ vendor_id: "", invoice_date: new Date(), delivery_fee: 0, discount: 0, customer_name: "", customer_phone: "", contact_id: "", rm_name: "", payee_name: "", payment_link: "" });
    setLineItems([{ ...initialLineItem }]);
    setGeneratedLink("");
  };

  if (showSuccess && createdInvoice) {
    return (
      <div className="max-w-lg mx-auto space-y-5 pt-6" data-testid="vendor-invoice-success">
        <div className="text-center">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Invoice Saved!</h1>
          <p className="text-slate-500 mt-1 text-sm">{createdInvoice.invoice_number} · {formData.customer_name}</p>
        </div>

        <Card className="border-slate-200">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-slate-900 text-sm">Customer invoice downloaded</p>
                <p className="text-xs text-slate-500 mt-0.5">PDF has been saved to your device</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-slate-900 text-sm">Vendor invoice — Merchant Signature Pending</p>
                <p className="text-xs text-slate-500 mt-0.5">Share the signing link below with {selectedVendor?.merchant_name} to collect their signature</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Merchant Signing Link</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="bg-slate-50 rounded-lg p-3 font-mono text-xs text-slate-700 break-all border border-slate-200">
              {signingLink}
            </div>
            <Button onClick={copyLink} className={`w-full ${copied ? "bg-emerald-600 hover:bg-emerald-700" : ""}`} variant={copied ? "default" : "outline"}>
              <Copy className="w-4 h-4 mr-2" />
              {copied ? "Copied!" : "Copy Signing Link"}
            </Button>
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={() => navigate("/vendor-history")}>View Vendor Invoices</Button>
          <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={resetForm}>Create Another</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="create-vendor-invoice-page">
      {/* Hidden Customer PDF Template */}
      <div style={{ position: "absolute", left: "-9999px", top: 0, zIndex: -1 }}>
        <div ref={customerPdfRef} style={{ width: "794px", padding: "56px 64px", backgroundColor: "#fff", fontFamily: "Arial, sans-serif", color: "#1e293b" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "32px", paddingBottom: "24px", borderBottom: "2px solid #e2e8f0" }}>
            <div>
              <h1 style={{ fontSize: "22px", fontWeight: "800", color: "#0f172a", margin: 0 }}>POKUS TECHNOLOGIES PVT LTD</h1>
              <p style={{ color: "#64748b", fontSize: "12px", marginTop: "4px" }}>GSTIN: 29AAQCP4908B1Z8</p>
              <p style={{ color: "#64748b", fontSize: "13px", marginTop: "2px" }}>Customer Invoice</p>
            </div>
            <div style={{ textAlign: "right" }}>
              <p style={{ fontSize: "11px", color: "#94a3b8", marginBottom: "2px", textTransform: "uppercase" }}>Date</p>
              <p style={{ fontWeight: "600", fontSize: "14px", color: "#1e293b" }}>{format(formData.invoice_date, "dd MMM yyyy")}</p>
            </div>
          </div>
          <div style={{ marginBottom: "28px" }}>
            <p style={{ fontSize: "11px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "6px" }}>Billed To</p>
            <p style={{ fontWeight: "700", fontSize: "16px", color: "#0f172a" }}>{formData.customer_name}</p>
            <p style={{ color: "#64748b", fontSize: "13px", marginTop: "2px" }}>{formData.customer_phone}</p>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "24px" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                {["Service", "Qty", "Rate (₹)", "Amount (₹)"].map((h, i) => (
                  <th key={h} style={{ padding: "10px 12px", fontSize: "11px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", textAlign: i === 0 ? "left" : "right" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lineItems.filter((i) => i.service_type && i.quantity > 0 && i.selling_price > 0).map((item, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "12px", color: "#1e293b", fontSize: "13px" }}>{item.custom_service || item.service_type}</td>
                  <td style={{ padding: "12px", textAlign: "right", color: "#475569", fontSize: "13px" }}>{item.quantity}</td>
                  <td style={{ padding: "12px", textAlign: "right", color: "#475569", fontSize: "13px" }}>{item.selling_price.toFixed(2)}</td>
                  <td style={{ padding: "12px", textAlign: "right", fontWeight: "600", color: "#0f172a", fontSize: "13px" }}>{(item.quantity * item.selling_price).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "32px" }}>
            <div style={{ width: "240px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#64748b", marginBottom: "6px" }}><span>Subtotal</span><span>₹{calculations.customer_subtotal.toFixed(2)}</span></div>
              {calculations.delivery_fee > 0 && <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#64748b", marginBottom: "6px" }}><span>Delivery Fee</span><span>+₹{calculations.delivery_fee.toFixed(2)}</span></div>}
              {calculations.discount > 0 && <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#ef4444", marginBottom: "6px" }}><span>Discount</span><span>-₹{calculations.discount.toFixed(2)}</span></div>}
              <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "800", fontSize: "16px", color: "#0f172a", borderTop: "2px solid #e2e8f0", paddingTop: "10px", marginTop: "6px" }}><span>Total Payable</span><span>₹{calculations.customer_total.toFixed(2)}</span></div>
            </div>
          </div>
          {(formData.rm_name || formData.payee_name) && (
            <div style={{ marginBottom: "24px", fontSize: "12px", color: "#64748b", display: "flex", gap: "24px" }}>
              {formData.rm_name && <span><strong>RM:</strong> {formData.rm_name}</span>}
              {formData.payee_name && <span><strong>Payee:</strong> {formData.payee_name}</span>}
            </div>
          )}
          {/* QR Code + Payment Link */}
          <div style={{ textAlign: "center", paddingTop: "20px", paddingBottom: "20px", borderTop: "1px solid #e2e8f0", marginBottom: "4px" }}>
            <p style={{ fontSize: "12px", fontWeight: "700", color: "#1e293b", marginBottom: "10px" }}>Scan to Pay</p>
            {qrCodeDataUrl && (
              <img src={qrCodeDataUrl} alt="Payment QR" style={{ width: "96px", height: "96px", margin: "0 auto 8px", display: "block" }} />
            )}
            <p style={{ fontSize: "11px", color: "#0047FF", fontWeight: "600" }}>{activePaymentLink}</p>
          </div>
          <div style={{ textAlign: "center", paddingTop: "16px", borderTop: "1px solid #f1f5f9", color: "#94a3b8", fontSize: "11px" }}>
            Thank you for your business! · Pokus Technologies Pvt Ltd
          </div>
        </div>
      </div>

      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Create Invoice</h1>
        <p className="text-slate-500 mt-1 text-sm">Generates customer invoice immediately + vendor invoice pending merchant signature</p>
      </div>

      <form onSubmit={handleSave}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          <div className="space-y-5">
            <Card className="border-slate-200">
              <CardHeader className="pb-3"><CardTitle className="text-base">Customer Details</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Customer Name *</Label>
                    <Input value={formData.customer_name} onChange={(e) => handleInputChange("customer_name", e.target.value)} placeholder="Customer name" data-testid="input-customer-name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone *</Label>
                    <Input value={formData.customer_phone} onChange={(e) => handleInputChange("customer_phone", e.target.value)} placeholder="Phone number" data-testid="input-customer-phone" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Contact ID</Label>
                    <Input value={formData.contact_id} onChange={(e) => handleInputChange("contact_id", e.target.value)} placeholder="Chatwoot contact ID" className="bg-slate-50 text-slate-500" data-testid="input-contact-id" />
                  </div>
                  <div className="space-y-2">
                    <Label>Invoice Date</Label>
                    <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start font-normal" data-testid="input-invoice-date">{format(formData.invoice_date, "PPP")}</Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar mode="single" selected={formData.invoice_date} onSelect={(d) => { handleInputChange("invoice_date", d || new Date()); setDatePickerOpen(false); }} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-3"><CardTitle className="text-base">Vendor</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Select Vendor *</Label>
                  <Select value={formData.vendor_id} onValueChange={handleVendorChange}>
                    <SelectTrigger data-testid="select-vendor"><SelectValue placeholder="Select a vendor" /></SelectTrigger>
                    <SelectContent>
                      {vendors.map((vendor) => (
                        <SelectItem key={vendor.id} value={vendor.id}>{vendor.merchant_name} — {vendor.service_type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {selectedVendor && (
                  <div className="p-3 bg-slate-50 rounded-lg space-y-1 text-sm">
                    <p><strong>Contact:</strong> {selectedVendor.contact_no}</p>
                    {selectedVendor.upi_id && <p><strong>UPI:</strong> {selectedVendor.upi_id}</p>}
                    {selectedVendor.address && <p><strong>Address:</strong> {selectedVendor.address}</p>}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Services</CardTitle>
                  <Button type="button" variant="outline" size="sm" onClick={addLineItem}><Plus className="w-4 h-4 mr-1" /> Add Item</Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wide px-1">
                  <div className="col-span-2">Item</div>
                  <div className="col-span-2 text-center">Qty</div>
                  <div className="col-span-2 text-right">CP (₹)</div>
                  <div className="col-span-2 text-right">SP (₹)</div>
                  <div className="col-span-2 text-right">Pokus Fee</div>
                  <div className="col-span-1 text-right">Vendor Amt</div>
                  <div className="col-span-1" />
                </div>
                {lineItems.map((item, index) => {
                  const needsCustom = item.service_type === "Other Services" || item.service_type === "Commerce";
                  const vendorAmt = item.quantity * item.cost_price;
                  const customerAmt = item.quantity * item.selling_price;
                  const pokusFee = customerAmt - vendorAmt;
                  return (
                    <div key={index} className="space-y-2" data-testid={`line-item-${index}`}>
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-2">
                          <Select value={item.service_type} onValueChange={(v) => handleLineItemChange(index, "service_type", v)}>
                            <SelectTrigger className="text-sm" data-testid={`input-service-${index}`}>
                              <SelectValue placeholder="Select service" />
                            </SelectTrigger>
                            <SelectContent>
                              {SERVICE_TYPES.map((t) => (
                                <SelectItem key={t} value={t}>{t}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2"><Input type="number" min="0" step="any" value={item.quantity} onChange={(e) => handleLineItemChange(index, "quantity", e.target.value)} onFocus={(e) => e.target.select()} className="font-mono text-sm text-center" data-testid={`input-qty-${index}`} /></div>
                        <div className="col-span-2"><Input type="number" min="0" step="0.01" value={item.cost_price} onChange={(e) => handleLineItemChange(index, "cost_price", e.target.value)} className="font-mono text-sm text-right" data-testid={`input-cp-${index}`} /></div>
                        <div className="col-span-2"><Input type="number" min="0" step="0.01" value={item.selling_price} onChange={(e) => handleLineItemChange(index, "selling_price", e.target.value)} className="font-mono text-sm text-right" data-testid={`input-sp-${index}`} /></div>
                        <div className={`col-span-2 font-mono text-xs text-right pr-1 ${pokusFee >= 0 ? "text-emerald-600" : "text-red-500"}`}>{formatCurrency(pokusFee)}</div>
                        <div className="col-span-1 font-mono text-xs text-slate-700 text-right pr-1">{formatCurrency(vendorAmt)}</div>
                        <div className="col-span-1 flex justify-center">
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeLineItem(index)} disabled={lineItems.length === 1} className="h-7 w-7 text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </div>
                      {needsCustom && (
                        <Input
                          value={item.custom_service}
                          onChange={(e) => handleLineItemChange(index, "custom_service", e.target.value)}
                          placeholder={`Specify ${item.service_type === "Commerce" ? "commerce" : "service"} description`}
                          className="text-sm col-span-12"
                          data-testid={`input-custom-service-${index}`}
                        />
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-3"><CardTitle className="text-base">Additional Details</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Delivery Fee (₹)</Label>
                    <Input type="number" min="0" value={formData.delivery_fee} onChange={(e) => handleInputChange("delivery_fee", parseFloat(e.target.value) || 0)} className="font-mono" data-testid="input-delivery-fee" />
                  </div>
                  <div className="space-y-2">
                    <Label>Discount (₹)</Label>
                    <Input type="number" min="0" value={formData.discount} onChange={(e) => handleInputChange("discount", parseFloat(e.target.value) || 0)} className="font-mono" data-testid="input-discount" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>RM</Label>
                    <Select value={formData.rm_name} onValueChange={(v) => handleInputChange("rm_name", v)}>
                      <SelectTrigger data-testid="input-rm-name"><SelectValue placeholder="Select RM" /></SelectTrigger>
                      <SelectContent>
                        {rmList.map((s) => <SelectItem key={s.id} value={s.full_name}>{s.full_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Payee</Label>
                    <Select value={formData.payee_name} onValueChange={(v) => handleInputChange("payee_name", v)}>
                      <SelectTrigger data-testid="input-payee-name"><SelectValue placeholder="Select Payee" /></SelectTrigger>
                      <SelectContent>
                        {payeeList.map((s) => <SelectItem key={s.id} value={s.full_name}>{s.full_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-3"><CardTitle className="text-base">Payment Link</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="flex-1">
                    <Label
                      htmlFor="auto_generate_link"
                      className="text-sm font-medium text-slate-700 cursor-pointer"
                    >
                      Auto-generate Razorpay link
                    </Label>
                    <p className="text-xs text-slate-500 mt-1">
                      {razorpayConfigured
                        ? "Creates a unique Razorpay Payment Link with the invoice amount & customer info, and attaches it to this invoice."
                        : "Configure RAZORPAY_KEY_ID / RAZORPAY_KEY_SECRET on the backend to enable this."}
                    </p>
                  </div>
                  <Switch
                    id="auto_generate_link"
                    checked={autoGenerateLink}
                    onCheckedChange={setAutoGenerateLink}
                    disabled={!razorpayConfigured}
                    data-testid="toggle-auto-razorpay"
                  />
                </div>

                {!autoGenerateLink && (
                  <>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="payment_link" className="text-sm font-medium text-slate-700">
                        Payment Link{" "}
                        <span className="text-xs text-slate-400 font-normal">
                          (optional — paste a Razorpay link)
                        </span>
                      </Label>
                      {formData.payment_link && (
                        <button
                          type="button"
                          onClick={() => handleInputChange("payment_link", "")}
                          className="text-xs text-slate-500 hover:text-slate-700 hover:underline"
                        >
                          Reset to default
                        </button>
                      )}
                    </div>
                    <Input
                      id="payment_link"
                      type="url"
                      value={formData.payment_link}
                      onChange={(e) => handleInputChange("payment_link", e.target.value)}
                      placeholder={DEFAULT_PAYMENT_LINK}
                      className="w-full"
                      data-testid="input-payment-link"
                    />
                    <p className="text-xs text-slate-500">
                      Customers see this on the invoice preview, PDF and QR code.
                      Leave blank to use the default{" "}
                      <code className="px-1 py-0.5 rounded bg-slate-100 text-slate-600">
                        razorpay.me/@pokus
                      </code>
                      .
                    </p>
                  </>
                )}

                {autoGenerateLink && (
                  <div className="text-xs text-slate-600 bg-blue-50 border border-blue-100 rounded-md px-3 py-2">
                    A Razorpay Payment Link will be generated with amount{" "}
                    <strong>₹{(calculations.customer_total || 0).toFixed(2)}</strong>{" "}
                    and customer <strong>{formData.customer_name || "—"}</strong>{" "}
                    once you save the invoice. The QR code & preview will update
                    automatically after generation.
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="lg:hidden">
              <Card className="border-slate-200 bg-slate-50">
                <CardContent className="p-4 space-y-2 text-sm">
                  <div className="flex justify-between text-slate-500"><span>Vendor Subtotal (CP)</span><span className="font-mono">{formatCurrency(calculations.vendor_subtotal)}</span></div>
                  <div className="flex justify-between text-slate-500"><span>Customer Subtotal (SP)</span><span className="font-mono">{formatCurrency(calculations.customer_subtotal)}</span></div>
                  <div className="flex justify-between text-emerald-600"><span>Pokus Fees</span><span className="font-mono">{formatCurrency(calculations.pokus_fees)}</span></div>
                  {calculations.delivery_fee > 0 && <div className="flex justify-between text-slate-500"><span>Delivery</span><span className="font-mono">+{formatCurrency(calculations.delivery_fee)}</span></div>}
                  {calculations.discount > 0 && <div className="flex justify-between text-red-500"><span>Discount (customer only)</span><span className="font-mono">-{formatCurrency(calculations.discount)}</span></div>}
                  <div className="flex justify-between font-bold text-slate-900 pt-2 border-t border-slate-200"><span>Vendor Total</span><span className="font-mono">{formatCurrency(calculations.vendor_total)}</span></div>
                  <div className="flex justify-between font-bold text-[#0047FF] pt-1"><span>Customer Total</span><span className="font-mono">{formatCurrency(calculations.customer_total)}</span></div>
                </CardContent>
              </Card>
            </div>

            <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base" data-testid="save-vendor-invoice-btn">
              {loading ? "Saving..." : <><Save className="w-5 h-5 mr-2" /> Save & Download</>}
            </Button>
          </div>

          <div className="hidden lg:block sticky top-6">
            <Card className="border-slate-200">
              <CardHeader className="pb-3 border-b border-slate-100">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Live Preview</p>
              </CardHeader>
              <CardContent className="pt-5 text-sm">
                <div className="text-center mb-4 pb-4 border-b border-slate-200">
                  <h2 className="text-base font-bold text-slate-900 uppercase">{selectedVendor?.merchant_name || "Vendor Name"}</h2>
                  {selectedVendor?.address && <p className="text-slate-400 text-xs mt-0.5">{selectedVendor.address}</p>}
                </div>
                {formData.customer_name && (
                  <div className="mb-3 text-xs">
                    <p className="text-slate-700"><strong>Customer:</strong> {formData.customer_name}</p>
                    {formData.customer_phone && <p className="text-slate-500">{formData.customer_phone}</p>}
                    <p className="text-slate-400 mt-1">{format(formData.invoice_date, "dd MMM yyyy")}</p>
                  </div>
                )}
                <table className="w-full text-xs mb-3">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left py-1.5 px-1 text-slate-400 font-medium">Item</th>
                      <th className="text-center py-1.5 px-1 text-slate-400 font-medium">Qty</th>
                      <th className="text-right py-1.5 px-1 text-slate-400 font-medium">CP</th>
                      <th className="text-right py-1.5 px-1 text-slate-400 font-medium">SP</th>
                      <th className="text-right py-1.5 px-1 text-slate-400 font-medium">Fee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((item, i) => {
                      const vAmt = item.quantity * item.cost_price;
                      const cAmt = item.quantity * item.selling_price;
                      return (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="py-2 px-1 text-slate-700">{item.custom_service || item.service_type || "—"}</td>
                          <td className="py-2 px-1 text-center font-mono">{item.quantity}</td>
                          <td className="py-2 px-1 text-right font-mono">{item.cost_price.toFixed(2)}</td>
                          <td className="py-2 px-1 text-right font-mono">{item.selling_price.toFixed(2)}</td>
                          <td className={`py-2 px-1 text-right font-mono ${cAmt - vAmt >= 0 ? "text-emerald-600" : "text-red-500"}`}>{(cAmt - vAmt).toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="flex justify-end mb-3">
                  <div className="w-52 border border-slate-200 rounded p-2 space-y-1 text-xs">
                    <div className="flex justify-between"><span className="text-slate-400">Vendor Subtotal</span><span className="font-mono">{calculations.vendor_subtotal.toFixed(2)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-400">Customer Subtotal</span><span className="font-mono">{calculations.customer_subtotal.toFixed(2)}</span></div>
                    <div className="flex justify-between text-emerald-600"><span>Pokus Fees</span><span className="font-mono">{calculations.pokus_fees.toFixed(2)}</span></div>
                    {calculations.delivery_fee > 0 && <div className="flex justify-between"><span className="text-slate-400">Delivery</span><span className="font-mono">+{calculations.delivery_fee.toFixed(2)}</span></div>}
                    {calculations.discount > 0 && <div className="flex justify-between"><span className="text-slate-400">Discount (customer only)</span><span className="font-mono text-red-500">-{calculations.discount.toFixed(2)}</span></div>}
                    <div className="flex justify-between font-bold pt-1.5 border-t border-slate-200 text-slate-900"><span>Vendor Total</span><span className="font-mono">{calculations.vendor_total.toFixed(2)}</span></div>
                    <div className="flex justify-between font-bold text-[#0047FF]"><span>Customer Total</span><span className="font-mono">{calculations.customer_total.toFixed(2)}</span></div>
                  </div>
                </div>
                {(formData.rm_name || formData.payee_name) && (
                  <div className="pt-3 border-t border-slate-100 text-xs text-slate-500 space-y-1">
                    {formData.rm_name && <p><strong>RM:</strong> {formData.rm_name}</p>}
                    {formData.payee_name && <p><strong>Payee:</strong> {formData.payee_name}</p>}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
};
